import ApiPath from "src/api/apiPath.json";
import PagePath from "src/pagePath.json";

/**
 * 실제 요청 URL 과 화면 경로를 템플릿으로 되돌린다.
 *
 * 태그에 실제 URL 을 넣으면 고유값이 리소스 개수만큼 늘어나 집계가 불가능해지므로,
 * 리소스 ID 가 박히지 않은 템플릿으로 바꿔서 내보낸다.
 *
 * **라우트 목록으로 routes.ts 은 금지**
 * 페이지 컴포넌트를 97개 import 하므로
 * 이 모듈에 앱 전체를 끌고 들어온다.
 */

type PathTree = { [key: string]: string | PathTree };
type CompiledTemplate = { template: string; pattern: RegExp };

/** 템플릿에서 파라미터를 찾는 패턴. split 에 쓰므로 캡처 그룹을 넣으면 안 된다 */
const API_PARAM_PATTERN = /\{[^}]+\}/g;
const PAGE_PARAM_PATTERN = /:[^/]+/g;

const REGEXP_SPECIAL_CHARS = /[.*+?^${}()|[\]\\]/g;

/** 중첩된 경로 트리를 훑어 경로 문자열만 평탄한 배열로 꺼낸다 */
const flattenPathTree = (tree: PathTree): string[] => {
  const templates: string[] = [];

  const walk = (node: string | PathTree) => {
    if (typeof node === "string") {
      templates.push(node);
      return;
    }
    Object.values(node).forEach(walk);
  };

  walk(tree);
  return templates;
};

/** 쿼리스트링을 떼고 경로만 남긴다. 절대 URL 이면 pathname 을 꺼낸다 */
const toPathname = (url: string): string => {
  const withoutQuery = url.split("?")[0];
  if (withoutQuery.startsWith("/")) return withoutQuery;

  try {
    return new URL(withoutQuery).pathname;
  } catch {
    return withoutQuery;
  }
};

const countParams = (template: string, paramPattern: RegExp) => (template.match(paramPattern) || []).length;

/**
 * 파라미터를 기준으로 잘라 리터럴 조각만 이스케이프하고, 그 사이를 [^/]+ 로 잇는다.
 * 한 문자열 위에서 이스케이프와 치환을 순차로 하면 서로를 망가뜨리므로 split 으로 분리한다.
 */
const buildPattern = (template: string, paramPattern: RegExp): RegExp => {
  const literals = template.split(paramPattern).map((literal) => literal.replace(REGEXP_SPECIAL_CHARS, "\\$&"));
  return new RegExp(`^${literals.join("[^/]+")}/?$`);
};

/**
 * 경로 트리를 정규식과 짝지어 미리 컴파일해 둔다. 모듈 로드 시 한 번만 실행된다.
 * 템플릿에도 쿼리스트링이 박힌 항목이 있어 경로만 남긴다.
 * 파라미터가 적은 것을 먼저 두어 리터럴 경로가 파라미터 경로보다 우선 매칭되게 한다.
 */
const compileTemplates = (tree: PathTree, paramPattern: RegExp): CompiledTemplate[] =>
  Array.from(new Set(flattenPathTree(tree).map(toPathname)))
    .sort((a, b) => countParams(a, paramPattern) - countParams(b, paramPattern) || b.length - a.length)
    .map((template) => ({ template, pattern: buildPattern(template, paramPattern) }));

const compiledApiPaths = compileTemplates(ApiPath as unknown as PathTree, API_PARAM_PATTERN);
const compiledPageRoutes = compileTemplates(PagePath as unknown as PathTree, PAGE_PARAM_PATTERN);

const findTemplate = (compiled: CompiledTemplate[], url: string | undefined): string | undefined => {
  if (!url) return undefined;

  const pathname = toPathname(url);
  return compiled.find(({ pattern }) => pattern.test(pathname))?.template;
};

export const matchApiPath = (url: string | undefined): string | undefined => findTemplate(compiledApiPaths, url);

export const matchPageRoute = (pathname: string | undefined): string | undefined => findTemplate(compiledPageRoutes, pathname);
